import { z } from "zod";
import {
  ContextError,
  projectContext,
  type HostContextProvider,
} from "./context";
import {
  hostJsonTransport,
  HostHttpError,
  type HostHttpOptions,
} from "./host-http";

export interface HttpContextOptions extends HostHttpOptions {}
const identifier = z.string().min(1).max(200);
const requestSchema = z
  .object({
    version: z.literal(1),
    tenantId: identifier,
    subjectId: identifier,
    projectId: identifier,
    projectRevision: z.number().int().nonnegative(),
    partId: identifier.optional(),
    clock: z.boolean(),
    parameters: z
      .record(
        z.string().min(1).max(100),
        z
          .record(
            z.string().min(1).max(100),
            z.union([z.string().max(1000), z.number(), z.boolean(), z.null()]),
          )
          .nullable(),
      )
      .optional(),
    forceRefresh: z.literal(true).optional(),
    sources: z
      .array(
        z
          .object({
            fieldId: identifier,
            kind: z.enum(["user", "order", "form", "dynamic"]),
            key: z.string().max(200),
            instanceId: z.string().max(200).optional(),
            dynamicMode: z.literal("choices").optional(),
            dynamicSourceId: z.string().min(1).max(100).optional(),
          })
          .strict(),
      )
      .max(1000),
  })
  .strict();
const invalidResponse = () =>
  new ContextError("CONTEXT_HTTP_INVALID_RESPONSE", 503);

/** Server-side transport. Call only with an authenticated host's ContextRequest. */
export function httpHostContextProvider(
  options: HttpContextOptions,
): HostContextProvider {
  let transport: ReturnType<typeof hostJsonTransport>;
  try {
    transport = hostJsonTransport(options, 1_000_000);
  } catch {
    throw new ContextError("CONTEXT_HTTP_CONFIGURATION_INVALID", 500);
  }
  return async (input, signal) => {
    const parsed = requestSchema.safeParse(input);
    if (!parsed.success) throw new ContextError("CONTEXT_HTTP_REQUEST_INVALID");
    try {
      const result = await transport(parsed.data, signal);
      try {
        if (
          !result ||
          typeof result !== "object" ||
          Array.isArray(result) ||
          (result as Record<string, unknown>).version !== 1 ||
          !Object.hasOwn(result, "context") ||
          Object.keys(result).some(
            (key) => !["version", "context"].includes(key),
          )
        )
          throw invalidResponse();
        return projectContext(
          parsed.data,
          (result as Record<string, unknown>).context,
        );
      } catch {
        throw invalidResponse();
      }
    } catch (error) {
      if (error instanceof ContextError) throw error;
      if (error instanceof HostHttpError) {
        if (error.reason === "cancelled")
          throw new ContextError("CONTEXT_CANCELLED", 499);
        if (error.reason === "timeout")
          throw new ContextError("CONTEXT_HTTP_TIMEOUT", 503);
        if (error.reason === "invalid-response") throw invalidResponse();
      }
      throw new ContextError("CONTEXT_PROVIDER_UNAVAILABLE", 503);
    }
  };
}
